import { ReturnRequestLogDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  MessageCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";
import ActivityContentWrapper from "./activity-content-wrapper";
import ActivityIconWrapper from "./activity-icon-wrapper";
import { ActivityItemHeader } from "./activity-item-header";
import ActivityWrapper from "./activity-wrapper";
import { getAuthorName } from "@/pages/(app)/returns/utils/get-author-name.ts";
import { Dialog, DialogContent } from "@babackapp/ui/components/dialog";
import { useEffect, useState, useMemo } from "react";
import { Button } from "@babackapp/ui/components/button";
import { convertHeicUrlToBlobUrl, isHeicUrl } from "@/utils/heic-image";
import { useActorName, useShopPeople } from "@/lib/people/use-shop-people";

type ImageLightboxProps = {
  images: string[];
  isOpen: boolean;
  currentIndex: number;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
};

function parseTextWithLinksAndMentions(
  text: string,
  memberNames: string[],
): React.ReactNode[] {
  // Build a regex that matches URLs or @memberName for known members
  // Escape special regex chars in names and sort by length (longest first) to avoid partial matches
  const escapedNames = memberNames
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));

  const mentionPattern =
    escapedNames.length > 0
      ? `@(?:${escapedNames.join("|")})(?=\\s|$|[.,;!?)])`
      : null;
  const urlPattern = "https?://[^\\s]+";
  const pattern = mentionPattern
    ? `(${urlPattern}|${mentionPattern})`
    : `(${urlPattern})`;
  const tokenRegex = new RegExp(pattern, "g");

  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(tokenRegex)) {
    const matchIndex = match.index;
    if (matchIndex > lastIndex) {
      result.push(text.slice(lastIndex, matchIndex));
    }

    const token = match[0];
    if (token.startsWith("http")) {
      result.push(
        <a
          key={matchIndex}
          href={token}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:text-primary/80"
        >
          {token}
        </a>,
      );
    } else {
      result.push(
        <span key={matchIndex} className="text-primary font-semibold">
          {token}
        </span>,
      );
    }

    lastIndex = matchIndex + token.length;
  }

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result.length > 0 ? result : [text];
}

type CommentActivityProps = {
  activity: ReturnRequestLogDto;
};

export default function CommentActivity({ activity }: CommentActivityProps) {
  const t = useTranslation();
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const attachments = useMemo(
    () => activity.attachments ?? [],
    [activity.attachments],
  );
  const [attachmentConversion, setAttachmentConversion] = useState(() => ({
    source: attachments,
    urls: attachments,
    isConverting: attachments.some(isHeicUrl),
  }));
  const isCurrentConversion = attachmentConversion.source === attachments;
  const displayedAttachments = isCurrentConversion
    ? attachmentConversion.urls
    : attachments;
  const isConvertingAttachments = isCurrentConversion
    ? attachmentConversion.isConverting
    : attachments.some(isHeicUrl);

  // Members and Baback support alike (BAB-3035).
  const people = useShopPeople();
  const actorName = useActorName();
  const author = actorName(activity.author);

  const memberNames = useMemo(
    () =>
      people
        .map((person) => getAuthorName(person.user))
        .filter((n): n is string => n !== null),
    [people],
  );

  const parsedComment = useMemo(() => {
    if (!activity.comment) return null;
    return parseTextWithLinksAndMentions(activity.comment, memberNames);
  }, [activity.comment, memberNames]);

  useEffect(() => {
    const createdBlobUrls: string[] = [];
    let cancelled = false;

    const convertAttachments = async () => {
      const convertedAttachments = await Promise.all(
        attachments.map(async (url) => {
          if (!isHeicUrl(url)) return url;

          try {
            const blobUrl = await convertHeicUrlToBlobUrl(url);
            createdBlobUrls.push(blobUrl);
            return blobUrl;
          } catch (error) {
            console.error("Error converting HEIC comment attachment:", error);
            return url;
          }
        }),
      );

      if (cancelled) {
        createdBlobUrls.forEach((url) => URL.revokeObjectURL(url));
        return;
      }

      setAttachmentConversion({
        source: attachments,
        urls: convertedAttachments,
        isConverting: false,
      });
    };

    void convertAttachments();

    return () => {
      cancelled = true;
      createdBlobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [attachments]);

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setIsLightboxOpen(true);
  };

  const goToPrevious = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? (activity.attachments?.length ?? 1) - 1 : prev - 1,
    );
  };

  const goToNext = () => {
    setCurrentImageIndex((prev) =>
      prev === (activity.attachments?.length ?? 1) - 1 ? 0 : prev + 1,
    );
  };

  return (
    <ActivityWrapper>
      <ActivityIconWrapper>
        <MessageCircleIcon className="size-4" />
      </ActivityIconWrapper>
      <ActivityContentWrapper>
        <ActivityItemHeader
          title={t("returnDetail.newNote")}
          timestamp={activity.date}
        />
        <div className="bg-muted rounded-2xl rounded-bl-none border border-border flex-1 min-w-[400px] p-2 flex gap-2">
          <div className="flex items-center justify-center bg-primary w-4 h-4 rounded-full">
            <span className="text-xs text-primary-foreground scale-75">
              {author?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="pt-0 -mt-2 flex-1">
            <span className="text-[11px] text-muted-foreground">{author}</span>
            <p className="font-medium mt-2 text-xs break-words">
              {parsedComment}
            </p>
            {displayedAttachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {isConvertingAttachments
                  ? displayedAttachments.map((_, index) => (
                      <div
                        key={index}
                        aria-label={`Converting attachment ${index + 1}`}
                        className="h-24 w-24 animate-pulse rounded border bg-muted-foreground/10"
                      />
                    ))
                  : displayedAttachments.map((url, index) => (
                      <button
                        key={index}
                        onClick={() => openLightbox(index)}
                        className="block cursor-pointer"
                      >
                        <img
                          src={url}
                          alt={`Attachment ${index + 1}`}
                          className="h-24 w-24 object-cover rounded border hover:opacity-80 transition-opacity"
                        />
                      </button>
                    ))}
              </div>
            )}
          </div>
        </div>
      </ActivityContentWrapper>

      {!isConvertingAttachments && displayedAttachments.length > 0 && (
        <ImageLightbox
          images={displayedAttachments}
          isOpen={isLightboxOpen}
          currentIndex={currentImageIndex}
          onClose={() => setIsLightboxOpen(false)}
          onPrevious={goToPrevious}
          onNext={goToNext}
        />
      )}
    </ActivityWrapper>
  );
}

function ImageLightbox({
  images,
  isOpen,
  currentIndex,
  onClose,
  onPrevious,
  onNext,
}: ImageLightboxProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 bg-black/95 border-none">
        <div className="relative flex items-center justify-center min-h-[400px] max-h-[90vh]">
          <img
            src={images[currentIndex]}
            alt={`Image ${currentIndex + 1}`}
            className="max-w-full max-h-[85vh] object-contain"
          />

          {images.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={onPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
              >
                <ChevronLeftIcon className="h-6 w-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
              >
                <ChevronRightIcon className="h-6 w-6" />
              </Button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded">
                {currentIndex + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
